import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setMainHeading } from "../../../redux/modules/mainHeading/slice";
import {
    Box, Grid, MenuItem, Select, Typography, Paper, Divider,
    CircularProgress, LinearProgress, Tooltip,
} from "@mui/material";
import { getRequest } from "../../../redux/modules/analytics/slice";
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

// Human-readable status labels
const STATUS_LABELS = {
    scheduled: 'Scheduled',
    in_progress: 'In Progress',
    images_received: 'Images Received',
    report_pending: 'Report Pending',
    report_complete: 'Report Complete',
    Requested: 'Requested',
    'Appointment Complete': 'Appointment Complete',
};

const STATUS_COLORS = {
    scheduled: '#64b5f6',
    in_progress: '#ffb74d',
    images_received: '#4fc3f7',
    report_pending: '#ff8a65',
    report_complete: '#81c784',
    Requested: '#ba68c8',
    'Appointment Complete': '#aed581',
};

// Format hours into a readable string
const formatHours = (hours) => {
    if (hours == null) return '-';
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    if (hours < 24) return `${hours}h`;
    const days = Math.round((hours / 24) * 10) / 10;
    return `${days}d`;
};

// Week label from firstDate
const weekLabel = (firstDate) => {
    if (!firstDate) return '';
    const d = new Date(firstDate);
    return `${d.getMonth() + 1}/${d.getDate()}`;
};

// --- Sub-components ---

const KpiCard = ({ icon, label, value, color, sub }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${color}18`,
                    color: color,
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {label}
            </Typography>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.5 }}>
            {value ?? '-'}
        </Typography>
        {sub && (
            <Typography variant="caption" color="text.secondary">
                {sub}
            </Typography>
        )}
    </Paper>
);

const SectionCard = ({ title, children, sx }) => (
    <Paper
        elevation={0}
        sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            ...sx,
        }}
    >
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {title}
            </Typography>
        </Box>
        <Divider />
        <Box sx={{ p: 3, flex: 1 }}>{children}</Box>
    </Paper>
);

// Simple bar chart using MUI boxes
const BarChart = ({ data, maxCount }) => {
    if (!data || data.length === 0) {
        return <Typography variant="body2" color="text.secondary">No data for this period</Typography>;
    }
    const max = maxCount || Math.max(...data.map(d => d.count), 1);
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 140, mt: 1 }}>
            {data.map((item, i) => {
                const height = Math.max((item.count / max) * 120, 4);
                return (
                    <Tooltip key={i} title={`Week of ${weekLabel(item.firstDate)}: ${item.count} orders`} arrow>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                {item.count}
                            </Typography>
                            <Box
                                sx={{
                                    width: '100%',
                                    maxWidth: 40,
                                    height,
                                    borderRadius: '4px 4px 0 0',
                                    bgcolor: 'primary.main',
                                    opacity: 0.8,
                                    transition: 'opacity 0.15s',
                                    '&:hover': { opacity: 1 },
                                }}
                            />
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                                {weekLabel(item.firstDate)}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
};

// --- Main Component ---

const Analytics = () => {
    const dispatch = useDispatch();
    const [selectedRange, setSelectedRange] = useState("thisMonth");
    let { loading, getRes } = useSelector(state => state.analytics);

    useEffect(() => {
        dispatch(setMainHeading("Analytics & Reporting"));
    }, [dispatch]);

    useEffect(() => {
        dispatch(getRequest({ range: selectedRange }));
    }, [dispatch, selectedRange]);

    const data = getRes?.success ? getRes : null;

    // Compute total for status percentages
    const statusTotal = useMemo(() => {
        return data?.ordersByStatus?.reduce((sum, s) => sum + s.count, 0) || 0;
    }, [data?.ordersByStatus]);

    const handleRangeChange = (event) => {
        setSelectedRange(event.target.value);
    };

    return (
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Analytics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Overview of clinic activity and performance
                    </Typography>
                </Box>
                <Select
                    size="small"
                    value={selectedRange}
                    onChange={handleRangeChange}
                    sx={{ minWidth: 160, borderRadius: 2 }}
                >
                    <MenuItem value="thisMonth">This Month</MenuItem>
                    <MenuItem value="lastMonth">Last Month</MenuItem>
                    <MenuItem value="last3Months">Last 3 Months</MenuItem>
                    <MenuItem value="last6Months">Last 6 Months</MenuItem>
                    <MenuItem value="thisYear">This Year</MenuItem>
                </Select>
            </Box>

            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

            {/* KPI Cards */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={6} md={4} lg={2}>
                    <KpiCard
                        icon={<AssignmentOutlinedIcon />}
                        label="Total Orders"
                        value={data?.totalOrders}
                        color="#1976d2"
                    />
                </Grid>
                <Grid item xs={6} md={4} lg={2}>
                    <KpiCard
                        icon={<PeopleOutlinedIcon />}
                        label="New Patients"
                        value={data?.totalPatients}
                        color="#7b1fa2"
                    />
                </Grid>
                <Grid item xs={6} md={4} lg={2}>
                    <KpiCard
                        icon={<CheckCircleOutlinedIcon />}
                        label="Completed Reports"
                        value={data?.completedReports}
                        color="#2e7d32"
                    />
                </Grid>
                <Grid item xs={6} md={4} lg={2}>
                    <KpiCard
                        icon={<PendingActionsOutlinedIcon />}
                        label="Pending Reports"
                        value={data?.pendingReports}
                        color="#ed6c02"
                    />
                </Grid>
                <Grid item xs={6} md={4} lg={2}>
                    <KpiCard
                        icon={<EditOutlinedIcon />}
                        label="Modified Reports"
                        value={data?.modifiedReports}
                        color="#0288d1"
                    />
                </Grid>
                <Grid item xs={6} md={4} lg={2}>
                    <KpiCard
                        icon={<TimerOutlinedIcon />}
                        label="Avg Turnaround"
                        value={data?.turnaround ? formatHours(data.turnaround.avgHours) : '-'}
                        color="#c62828"
                        sub={data?.turnaround
                            ? `Min ${formatHours(data.turnaround.minHours)} / Max ${formatHours(data.turnaround.maxHours)} (n=${data.turnaround.sampleSize})`
                            : 'No completed reports'}
                    />
                </Grid>
            </Grid>

            {/* Charts Row */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                {/* Orders Over Time */}
                <Grid item xs={12} md={7}>
                    <SectionCard title="Orders Over Time (by week)">
                        {data?.ordersOverTime ? (
                            <BarChart data={data.ordersOverTime} />
                        ) : (
                            !loading && <Typography variant="body2" color="text.secondary">No data</Typography>
                        )}
                    </SectionCard>
                </Grid>

                {/* Order Status Breakdown */}
                <Grid item xs={12} md={5}>
                    <SectionCard title="Order Status Breakdown">
                        {data?.ordersByStatus && data.ordersByStatus.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {data.ordersByStatus.map((item) => {
                                    const pct = statusTotal > 0 ? Math.round((item.count / statusTotal) * 100) : 0;
                                    const color = STATUS_COLORS[item._id] || '#90a4ae';
                                    return (
                                        <Box key={item._id}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {STATUS_LABELS[item._id] || item._id || 'Unknown'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {item.count} ({pct}%)
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={pct}
                                                sx={{
                                                    height: 8,
                                                    borderRadius: 4,
                                                    bgcolor: `${color}20`,
                                                    '& .MuiLinearProgress-bar': {
                                                        borderRadius: 4,
                                                        bgcolor: color,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : (
                            !loading && <Typography variant="body2" color="text.secondary">No orders in this period</Typography>
                        )}
                    </SectionCard>
                </Grid>
            </Grid>

            {/* Top Exams */}
            <Grid container spacing={2.5}>
                <Grid item xs={12}>
                    <SectionCard title="Top Exams Performed">
                        {data?.topExams && data.topExams.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {(() => {
                                    const maxExamCount = Math.max(...data.topExams.map(e => e.count), 1);
                                    return data.topExams.map((exam, idx) => {
                                        const pct = Math.round((exam.count / maxExamCount) * 100);
                                        return (
                                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        width: 24,
                                                        fontWeight: 600,
                                                        color: idx < 3 ? 'primary.main' : 'text.secondary',
                                                        textAlign: 'right',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {idx + 1}
                                                </Typography>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                                            {exam.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
                                                            {exam.count}
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={pct}
                                                        sx={{
                                                            height: 6,
                                                            borderRadius: 3,
                                                            bgcolor: 'grey.100',
                                                            '& .MuiLinearProgress-bar': {
                                                                borderRadius: 3,
                                                                bgcolor: idx < 3 ? 'primary.main' : 'grey.400',
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        );
                                    });
                                })()}
                            </Box>
                        ) : (
                            !loading && <Typography variant="body2" color="text.secondary">No exam data for this period</Typography>
                        )}
                    </SectionCard>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Analytics;
